import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import {
  emptyTierDraft,
  PricingHistoryList,
  type PricingRevision,
  rateToTierDraft,
  type SessionRate,
  type TierDraft,
  TierEditor,
  TierReadonlyList,
  tiersToRatePayload,
} from "../components/TierEditor.js";
import { isFleetAdmin } from "../hooks/useFleetAccess.js";
import { useFleetOptions } from "../hooks/useFleetOptions.js";

interface OverridingVenue {
  id: string;
  slug: string;
  name: string;
}

export function Pricing() {
  const { user } = useAuth();
  const { options: fleetOptions, loaded: fleetsLoaded, error: fleetsError } = useFleetOptions();
  const search = useSearch();
  const [, navigate] = useLocation();
  const queryFleetId = new URLSearchParams(search).get("fleet_id") ?? "";
  const fleetId =
    queryFleetId && fleetOptions.some((f) => f.id === queryFleetId)
      ? queryFleetId
      : (fleetOptions[0]?.id ?? null);

  const canAdmin = user && fleetId ? isFleetAdmin(user, fleetId) : false;

  const [tiers, setTiers] = useState<TierDraft[]>([]);
  const [overridingVenues, setOverridingVenues] = useState<OverridingVenue[]>([]);
  const [history, setHistory] = useState<PricingRevision[]>([]);
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const selectFleet = useCallback(
    (id: string) => {
      navigate(`?fleet_id=${id}`, { replace: true });
    },
    [navigate],
  );

  const fetchRates = useCallback(async (id: string) => {
    try {
      const res = await apiClient["/api/v1/admin/fleets/{id}/pricing"]["get"]({
        parameters: { id },
        body: null,
      });
      if (res.code === 200) {
        const body = res.body as {
          rates: SessionRate[];
          overriding_venues: OverridingVenue[];
          history: PricingRevision[];
        };
        const activeRates = body.rates.filter((r) => r.is_active);
        if (activeRates.length > 0) {
          setTiers(activeRates.map(rateToTierDraft));
        } else {
          setTiers([emptyTierDraft(0)]);
        }
        setOverridingVenues(body.overriding_venues ?? []);
        setHistory(body.history ?? []);
      }
    } catch {
      setError("Failed to load rates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (fleetId) {
      setLoading(true);
      fetchRates(fleetId);
    }
  }, [fleetId, fetchRates]);

  const handleTiersChange = useCallback((next: TierDraft[]) => {
    setTiers(next);
    setSuccess(false);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!fleetId) {
        return;
      }
      setError(null);
      setSuccess(false);

      try {
        const rates = tiersToRatePayload(tiers);
        setSubmitting(true);
        const res = await apiClient["/api/v1/admin/fleets/{id}/pricing"]["put"]({
          parameters: { id: fleetId },
          body: { rates } as never,
        });
        if (res.code === 200) {
          setSuccess(true);
          await fetchRates(fleetId);
        } else {
          const body = res.body as { message?: string };
          setError(body.message ?? "Failed to update rates");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Network error");
      } finally {
        setSubmitting(false);
      }
    },
    [tiers, fleetId, fetchRates],
  );

  const overrideCount = overridingVenues.length;

  return (
    <main className="p-6">
      <h1 className="text-2xl font-bold text-text-primary mb-6">Pricing</h1>

      {fleetOptions.length > 1 && (
        <div className="mb-4">
          <select
            value={fleetId ?? ""}
            onChange={(e) => selectFleet(e.target.value)}
            className="bg-surface border border-border rounded px-2 py-1 text-sm"
          >
            {fleetOptions.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {!fleetsLoaded && <p className="text-text-muted text-sm">Loading...</p>}

      {fleetsLoaded && fleetsError && <p className="text-sm text-red-400">Failed to load fleets</p>}

      {fleetsLoaded && !fleetsError && fleetOptions.length === 0 && (
        <p className="text-text-muted text-sm">No fleets available.</p>
      )}

      {fleetsLoaded && !fleetsError && fleetOptions.length > 0 && loading && (
        <p className="text-text-muted text-sm">Loading...</p>
      )}

      {fleetsLoaded && !fleetsError && fleetOptions.length > 0 && !loading && fleetId && (
        <div className="space-y-6">
          {overrideCount > 0 && (
            <div className="max-w-2xl rounded-md border border-yellow-500/30 bg-yellow-500/10">
              <button
                type="button"
                onClick={() => setOverridesOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
              >
                <span className="text-sm text-yellow-200">
                  {overrideCount} {overrideCount === 1 ? "venue is" : "venues are"} overriding fleet
                  price
                </span>
                <span className="text-yellow-200/80 text-xs">{overridesOpen ? "▼" : "▶"}</span>
              </button>
              {overridesOpen && (
                <ul className="border-t border-yellow-500/20 px-4 py-2 space-y-1">
                  {overridingVenues.map((v) => (
                    <li key={v.id}>
                      <Link
                        href={`/venues/${v.slug}#pricing`}
                        className="text-sm text-yellow-100 hover:underline"
                      >
                        {v.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {canAdmin ? (
            <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
              <TierEditor tiers={tiers} onChange={handleTiersChange} disabled={submitting} />

              {error && <p className="text-sm text-red-400">{error}</p>}
              {success && <p className="text-sm text-green-400">Pricing updated.</p>}

              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Saving..." : "Save Pricing"}
              </button>
            </form>
          ) : (
            <div className="max-w-2xl">
              <TierReadonlyList tiers={tiers} />
            </div>
          )}

          <PricingHistoryList history={history} />
        </div>
      )}
    </main>
  );
}
